import { useEffect, useState, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { PageHeader } from '@/components/admin/AdminShell';
import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import {
  createThread,
  postMessage,
  uploadMessageAttachment,
} from '@/features/messages/api';
import { ApiError } from '@/lib/api/http';

function isImageFileName(name: string): boolean {
  return /\.(jpe?g|png|gif|webp|bmp)$/i.test(name);
}

/**
 * スレッド新規作成（指示書 §12 M8 / D-120・D-134・D-138）。
 * 受講生・理事・サクセスは「事務局への問い合わせ」を作成できる（相手は常に事務局）。
 * 事務局は「連絡先」一覧（/messages）から相手を選んでここに来る（recipient・nameをクエリで渡す）。
 * 一斉配信はここでは扱わない。事務局が全員・ロール単位に送りたいときは
 * 「システム通知」の「手入力の通知」を使う（D-138）。
 * サクセス面談・1on1の連絡はイベントごとのグループメッセージで行うため、ここでは作らない。
 */
export function ThreadCreatePage(): ReactElement {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOffice = user?.active_role === 'office';
  const isStaffOfficeChat =
    user?.active_role === 'director' || user?.active_role === 'success';
  const [searchParams] = useSearchParams();
  const recipientUserId = searchParams.get('recipient') ?? '';
  const recipientName = searchParams.get('name') ?? '';

  const [body, setBody] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!file || !isImageFileName(file.name)) {
      setFilePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setFilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  async function onSubmit(): Promise<void> {
    const text = body.trim();
    if (!text && !file) return;
    setSubmitting(true);
    setError(null);
    try {
      const thread = await createThread({
        kind: 'direct',
        // API は本文必須。ファイルのみのときはプレースホルダを置き、続けて添付を投稿する（D-295）
        body: text || '（画像・ファイルを送信しました）',
        ...(isOffice ? { recipient_user_id: recipientUserId } : {}),
      });
      if (file) {
        const attachment = await uploadMessageAttachment(thread.id, file);
        await postMessage(thread.id, '', attachment);
      }
      navigate(`/messages/${thread.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'スレッドの作成に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit =
    (body.trim().length > 0 || file !== null) && (!isOffice || recipientUserId !== '');

  return (
    <>
      <PageHeader
        title={
          isOffice
            ? recipientName
              ? `${recipientName}さんへ送る`
              : 'メッセージを送る'
            : isStaffOfficeChat
              ? '事務局に連絡'
              : '事務局への問い合わせ'
        }
      />
      <MetaBox title="内容">
        {error && (
          <Notice kind="error" dismissible={false}>
            {error}
          </Notice>
        )}
        {isOffice && !recipientUserId && (
          <Notice kind="error" dismissible={false}>
            送り先が指定されていません。「連絡先」の一覧から相手を選んでください。
          </Notice>
        )}
        {!isOffice && (
          <Notice kind="info" dismissible={false}>
            {isStaffOfficeChat ? (
              <>
                ここで送った内容は<strong>事務局</strong>に届きます。画像やファイルも添付できます。
                イベント参加者とのグループ連絡は、各イベント詳細のメッセージタブから開けます。
              </>
            ) : (
              <>
                ここで送った内容は<strong>事務局の共有受信箱</strong>に届きます。事務局以外の個人
                （ほかの受講生・理事講師・サクセス担当）へ直接メッセージを送ることはできません。
                サクセス面談・1on1の連絡は、その予定が入ると自動で作られる
                <strong>グループメッセージ</strong>をご利用ください。
              </>
            )}
          </Notice>
        )}

        <label className="mb-3 block">
          <span className="mb-1.5 block text-wptext-muted">本文</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            autoFocus
            className="w-full max-w-xl rounded-sm border border-wpborder px-3 py-2"
          />
        </label>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <label
            htmlFor="thread-create-file"
            className="cursor-pointer rounded-sm border border-wpborder bg-white px-3.5 py-2 text-wptext-strong hover:bg-wpcanvas"
          >
            画像・ファイルを添付
          </label>
          <input
            id="thread-create-file"
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/bmp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="sr-only"
          />
          {file && (
            <>
              {filePreviewUrl ? (
                <img
                  src={filePreviewUrl}
                  alt={file.name}
                  className="h-16 w-16 rounded-sm object-cover"
                />
              ) : null}
              <span className="text-wptext-muted">{file.name}</span>
              <button
                type="button"
                onClick={() => setFile(null)}
                className="text-wpnotice-error hover:underline"
              >
                取り消す
              </button>
            </>
          )}
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            disabled={submitting || !canSubmit}
            onClick={() => void onSubmit()}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            {submitting ? '送信中…' : '送信する'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/messages')}
            className="btn-secondary"
          >
            キャンセル
          </button>
        </div>
      </MetaBox>
    </>
  );
}
